import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

function WalletCard() {
    const [wallet, setWallet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showPayoutModal, setShowPayoutModal] = useState(false);
    const [payoutAmount, setPayoutAmount] = useState('');
    const [requesting, setRequesting] = useState(false);

    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchWallet();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchWallet = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/worker/wallet`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setWallet(res.data);
        } catch (err) {
            console.error('Failed to load wallet:', err);
            toast.error('Failed to load wallet data.');
        }
        setLoading(false);
    };

    const handleRequestPayout = async () => {
        if (!payoutAmount || parseFloat(payoutAmount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        setRequesting(true);
        try {
            await axios.post(
                `${API_URL}/api/worker/payouts/request`,
                { amount: parseFloat(payoutAmount) },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Payout requested successfully!');
            setShowPayoutModal(false);
            setPayoutAmount('');
            fetchWallet();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to request payout');
        }
        setRequesting(false);
    };

    if (loading) {
        return <div className="card" style={{ padding: '24px', color: 'var(--text-muted)' }}>Loading wallet...</div>;
    }

    if (!wallet) {
        return null;
    }

    return (
        <>
            <div className="card card-hover" style={{ padding: '24px', marginBottom: '32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '18px', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        💰 Your Wallet
                    </h3>
                    <span className="badge">USD</span>
                </div>

                {/* TWO COLUMNS FOR BALANCE AND ESCROW */}
                <div style={{ display: 'grid', gridTemplateColumns: wallet.escrowBalance !== undefined ? '1fr 1fr' : '1fr', gap: '16px', marginBottom: '24px' }}>
                    
                    {/* AVAILABLE BALANCE */}
                    <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--border)', padding: '16px', borderRadius: 'var(--radius-lg)' }}>
                        <span style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Available Balance</span>
                        <span style={{ fontSize: '28px', fontWeight: '600', color: 'var(--text)', letterSpacing: '-0.03em' }}>
                            ${parseFloat(wallet.balance || 0).toFixed(2)}
                        </span>
                    </div>

                    {/* ESCROW BALANCE (Only shows if it exists in the wallet object) */}
                    {wallet.escrowBalance !== undefined && (
                        <div style={{ background: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.2)', padding: '16px', borderRadius: 'var(--radius-lg)' }}>
                            <span style={{ fontSize: '13px', color: 'var(--warning)', display: 'block', marginBottom: '8px' }}>Locked in Escrow</span>
                            <span style={{ fontSize: '28px', fontWeight: '600', color: 'var(--warning)', letterSpacing: '-0.03em' }}>
                                ${parseFloat(wallet.escrowBalance || 0).toFixed(2)}
                            </span>
                        </div>
                    )}
                </div>

                <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ fontSize: '14px', color: 'var(--text)', marginBottom: '12px' }}>Recent Transactions</h4>
                    {wallet.recentTransactions && wallet.recentTransactions.length > 0 ? (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {wallet.recentTransactions.slice(0, 5).map((tx) => (
                                <li key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                                    <div>
                                        <span style={{ fontWeight: '500', fontSize: '14px', color: tx.amount >= 0 ? 'var(--success)' : 'var(--error)', marginRight: '12px' }}>
                                            {tx.amount >= 0 ? '+' : ''}${parseFloat(tx.amount).toFixed(2)}
                                        </span>
                                        <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{tx.description || tx.type}</span>
                                    </div>
                                    <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                                        {new Date(tx.createdAt).toLocaleDateString()}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>No transactions yet</p>
                    )}
                </div>

                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button 
                        onClick={() => setShowPayoutModal(true)}
                        className="btn-primary"
                        disabled={!wallet.balance || parseFloat(wallet.balance) <= 0}
                    >
                        Request Payout
                    </button>
                    <Link to="/worker/transactions" className="btn-secondary" style={{ padding: '10px 16px', fontSize: '13px' }}>
                        Transactions →
                    </Link>
                    <Link to="/worker/payouts" className="btn-secondary" style={{ padding: '10px 16px', fontSize: '13px' }}>
                        Payouts →
                    </Link>
                </div>
            </div>

            {/* Payout Modal */}
            {showPayoutModal && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }} onClick={() => setShowPayoutModal(false)}>
                    <div className="card" style={{ width: '100%', maxWidth: '400px', padding: '32px' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ fontSize: '20px', marginBottom: '8px', color: 'var(--text)' }}>Request Payout</h3>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
                            Available to withdraw: <strong style={{ color: 'var(--text)' }}>${parseFloat(wallet.balance || 0).toFixed(2)}</strong>
                        </p>
                        
                        <div className="form-field">
                            <label className="form-label">Amount (USD)</label>
                            <input
                                type="number"
                                placeholder="0.00"
                                value={payoutAmount}
                                onChange={(e) => setPayoutAmount(e.target.value)}
                                min="1"
                                step="0.01"
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                            <button 
                                onClick={() => setShowPayoutModal(false)} 
                                className="btn-secondary"
                                disabled={requesting}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleRequestPayout} 
                                className="btn-primary"
                                disabled={requesting}
                            >
                                {requesting ? 'Requesting...' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default WalletCard;